import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// After a deploy, a client-side navigation into a lazy(() => import(...)) route
// can still be holding the previous build's chunk map, so the browser requests
// a JS file that no longer exists on the CDN. That throws here as a plain
// runtime error, and clicking "retry" (a setState reset) can't fix it — the
// stale chunk reference is still stale. Only a real reload re-fetches index.html
// with the current chunk map. This is the underlying cause of "clicking around
// does nothing, only a hard refresh works": detect that specific failure and
// recover automatically instead of stranding the user on an error screen.
const CHUNK_LOAD_ERROR_RE = /fetch dynamically imported module|failed to fetch dynamically|error loading dynamically imported module|importing a module script failed|chunkloaderror/i;

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  return CHUNK_LOAD_ERROR_RE.test(error.message) || CHUNK_LOAD_ERROR_RE.test(error.name);
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // In production: send to Sentry/LogRocket
    console.error('[ErrorBoundary]', error, info.componentStack);

    // Reload once automatically on a stale-chunk failure so the user never
    // has to manually hard-refresh. The sessionStorage flag prevents a loop
    // if the reload somehow lands on the same stale error again.
    if (isChunkLoadError(error) && !sessionStorage.getItem('chunk-reload-attempted')) {
      sessionStorage.setItem('chunk-reload-attempted', '1');
      window.location.reload();
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const err = this.state.error;
    const path = window.location.pathname;

    return (
      <div style={{
        minHeight: '60vh', display: 'flex', flexDirection: 'column',
        alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px', maxWidth: 680, margin: '0 auto',
        fontFamily: 'var(--font-mono)',
      }}>
        {/* Compiler-style error header */}
        <div style={{ color: '#F0665C', fontSize: 14, fontWeight: 700, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>error[E500]:</span>
          <span style={{ color: '#EFE9F7' }}>unexpected runtime error</span>
        </div>

        {/* File/line reference */}
        <div style={{ color: '#6A9955', fontSize: 13, marginBottom: 20 }}>
          {' --> '}{path}:0:0
        </div>

        {/* Error message block */}
        <div style={{
          background: '#0C0A13', border: '1px solid rgba(248,81,73,0.25)',
          borderRadius: 6, padding: '16px 20px', width: '100%', marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <span style={{ color: '#F0665C', minWidth: 24 }}>|</span>
            <div>
              <div style={{ color: '#A79BC2', fontSize: 12, marginBottom: 6 }}>
                {err?.name ?? 'Error'}: {err?.message ?? 'An unexpected error occurred'}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <span style={{ color: '#F0665C', minWidth: 24 }}>|</span>
                <span style={{ color: '#F0665C', fontSize: 12 }}>
                  {'^ '.repeat(Math.min((err?.message?.length ?? 20), 40))}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Help text */}
        <div style={{ color: '#A79BC2', fontSize: 13, marginBottom: 24, lineHeight: 1.65 }}>
          <span style={{ color: '#7BC48F' }}>= help:</span> refresh the page or navigate home to recover
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              padding: '8px 18px', background: '#8A5CF6', border: 'none',
              borderRadius: 4, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
            }}
          >
            $ retry
          </button>
          <Link
            to="/"
            style={{
              padding: '8px 18px', background: 'transparent',
              border: '1px solid rgba(240,246,252,0.12)',
              borderRadius: 4, color: '#A79BC2', fontSize: 13,
              textDecoration: 'none', fontFamily: 'var(--font-mono)',
            }}
          >
            $ cd /
          </Link>
        </div>
      </div>
    );
  }
}

/** Lightweight wrapper for route-level boundaries */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundary>{children}</ErrorBoundary>;
}
